import { RiDeleteBin5Line } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import { DoubleColumnProductList } from '@/components/features';
import { CollapsibleBottomSheet } from '@/components/layout';
import { deleteList, getListProducts } from '@/services/slaydAPI';
import useSlaylistStore from '@/stores/SlaylistProductStore';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useToastStore from '@/stores/ToastStore';
import type { Product } from '@/types/Product.types';
import styles from './SlaylistView.module.css';

interface SlaylistViewProps {
  products?: Product[];
  listId?: number;
  title?: string;
}

interface LocationState {
  id?: number;
  title?: string;
  products?: Product[];
}

interface DeleteSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmationSheet = ({
  isOpen,
  onClose,
  onConfirm,
}: DeleteSheetProps): JSX.Element => {
  if (!isOpen) {
    return <></>;
  }

  return (
    <CollapsibleBottomSheet
      isExpanded
      maxHeight={210}
      onExpandedChange={(expanded) => !expanded && onClose()}
    >
      <div className={styles.deleteModalContent}>
        <div className={styles.deleteModalTitle}>Confirm delete</div>
        <div className={styles.deleteModalDesc}>
          This will permanently remove the list. You won&apos;t be able to
          recover it.
        </div>
      </div>
      <div className={styles.deleteModalActions}>
        <button className={styles.deleteModalCancel} onClick={onClose}>
          Cancel
        </button>
        <button className={styles.deleteModalDelete} onClick={onConfirm}>
          Delete
        </button>
      </div>
    </CollapsibleBottomSheet>
  );
};

const SlaylistView = ({
  title: propTitle = 'List',
  listId: propListId = 0,
}: SlaylistViewProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { showToast } = useToastStore();

  // Use Zustand store
  const { slaylistProducts, setSlaylistProducts, setSlaylistId } =
    useSlaylistStore();
  const {
    selectedSlaylist,
    setSelectedSlaylist,
    getSlaylistIdbyName,
    removeSlaylist,
    productSlaylistOverrides,
    removeProductSlaylist,
  } = useSlaylistSelectorStore();

  // Extract listId from URL pathname (e.g., /slaylist/123 -> "123")
  const listIdFromUrl = location.pathname.split('/').at(-1);
  const listId = listIdFromUrl
    ? parseInt(listIdFromUrl, 10)
    : locationState?.id || propListId;
  const [title, setTitle] = useState<string>(propTitle);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentListId, setCurrentListId] = useState<number | null>(null);
  const [showDeleteBtn, setShowDeleteBtn] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchProducts = useCallback(
    async (id: number) => {
      if (!id) return;

      // Clear products immediately when starting to fetch for a new list
      setSlaylistProducts([]);
      setCurrentListId(id);
      setSlaylistId(id); // Update the store's list ID
      setIsLoading(true);
      setError(null);

      try {
        const fetchedProducts = await getListProducts(id);
        const reversedProducts = fetchedProducts?.reverse();

        // Only update products if we're still on the same list ID
        // This prevents race conditions if user navigates quickly
        if (id === parseInt(location.pathname.split('/').at(-1) || '0', 10)) {
          setSlaylistProducts(reversedProducts || []);
        }
      } catch (err) {
        // Only show error if we're still on the same list ID
        if (id === parseInt(location.pathname.split('/').at(-1) || '0', 10)) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to fetch products';
          setError(errorMessage);
          console.error('Error fetching products:', err);
        }
      } finally {
        if (id === parseInt(location.pathname.split('/').at(-1) || '0', 10)) {
          setIsLoading(false);
        }
      }
    },
    [setSlaylistProducts, setSlaylistId, location.pathname]
  );

  useEffect(() => {
    // Update title from navigation state if available
    if (locationState?.title) {
      setTitle(locationState.title);
    }

    // Hide delete button if title is 'Saved'
    if ((locationState?.title || propTitle) === 'Saved') {
      setShowDeleteBtn(false);
    } else {
      setShowDeleteBtn(true);
    }

    // Fetch products whenever listId changes (including URL changes)
    if (listId && !isNaN(listId) && listId !== currentListId) {
      fetchProducts(listId);
    }
  }, [
    listId,
    locationState?.title,
    fetchProducts,
    currentListId,
    location.pathname,
    propTitle,
  ]);

  // Clear products when component unmounts or listId changes
  useEffect(() => {
    return () => {
      // optional: clear products when component unmounts
      // setSlaylistProducts([]);
    };
  }, []);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleDelete = (): void => {
    setShowDeleteModal(true);
  };

  const handleCancelDelete = (): void => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setShowDeleteModal(false);
    try {
      navigate('/slaylist', { replace: true }); // prevents going back to deleted list
      showToast('List deleted successfully');
      // if the selected list is the one that is being deleted, set the selected list to nothing
      // this reopens the selector next time
      if (getSlaylistIdbyName(selectedSlaylist) === listId) {
        setSelectedSlaylist('');
      }
      await deleteList(listId);

      // Remove the slaylist from the store
      removeSlaylist(listId);

      // Remove all product IDs associated with this slaylist from the store
      Object.entries(productSlaylistOverrides).forEach(
        ([productId, override]) => {
          if (override?.id === listId) {
            removeProductSlaylist(Number(productId));
          }
        }
      );

      // Optionally show a success toast here
    } catch (error) {
      // Optionally show an error toast here
      console.error('Failed to delete list:', error);
    }
  };

  // Show loading state only when actually fetching
  const productsToShow = slaylistProducts;

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <button className={styles.backButton} onClick={handleGoBack}>
              <img
                src={backButtonDark}
                alt="Back"
                className={styles.backIcon}
              />
            </button>
            <div className={styles.title}>
              {title === 'Saved' ? 'All Saves' : title}
            </div>
            {showDeleteBtn && (
              <button className={styles.deleteButton} onClick={handleDelete}>
                <RiDeleteBin5Line size={24} />
              </button>
            )}
          </div>
        </div>
        <div className={styles.productList}>
          <p>Error loading products: {error}</p>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <button className={styles.backButton} onClick={handleGoBack}>
            <img src={backButtonDark} alt="Back" className={styles.backIcon} />
          </button>
          <div className={styles.title}>{title}</div>
          {showDeleteBtn && (
            <button className={styles.deleteButton} onClick={handleDelete}>
              <RiDeleteBin5Line size={24} />
            </button>
          )}
        </div>
      </div>
      <div className={styles.productList}>
        {isLoading ? (
          <div className={styles.loading}>Loading products...</div>
        ) : productsToShow.length === 0 ? (
          <div className={styles.noProducts}>No products in this list</div>
        ) : (
          <DoubleColumnProductList
            products={[...productsToShow].reverse()}
            isLoading={false}
            hasMore={false}
            onLoadMore={() => {}}
          />
        )}
      </div>

      {showDeleteModal && (
        <div className={styles.deleteModalOverlay} onClick={handleCancelDelete}>
          {createPortal(
            <DeleteConfirmationSheet
              isOpen={showDeleteModal}
              onClose={handleCancelDelete}
              onConfirm={handleConfirmDelete}
            />,
            document.body
          )}
        </div>
      )}
    </div>
  );
};

export default SlaylistView;
